import React, { memo, useCallback } from "react";
import { Modal, Button } from "@repo/core-ui";
import {
  IndianRupee,
  Calendar,
  Lock,
  Users,
  Home,
  FileText,
  Activity,
  Clock,
} from "lucide-react";
import { SubscriptionPlan } from "@repo/types";

interface SubscriptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: SubscriptionPlan | null;
}

export const SubscriptionDetailsModal: React.FC<SubscriptionDetailsModalProps> =
  memo(({ isOpen, onClose, plan }) => {
    const renderDetailItem = useCallback(
      (label: string, value: any, icon?: React.ReactNode) => (
        <div className="flex flex-col gap-1 p-5 rounded-2xl bg-white dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 transition-all hover:bg-slate-50 dark:hover:bg-[#1b254b] hover:border-slate-300 dark:hover:border-white/20 shadow-xs">
          <div className="flex items-center gap-2">
            {icon && (
              <span className="text-slate-400 dark:text-slate-500">{icon}</span>
            )}
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              {label}
            </span>
          </div>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
            {value ?? (
              <span className="text-slate-400 dark:text-slate-600 italic font-medium">
                Not Provided
              </span>
            )}
          </div>
        </div>
      ),
      [],
    );

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        title="Subscription Plan Details"
        maxWidth="max-w-2xl"
        maxHeight="max-h-[90vh] sm:max-h-[85vh]"
        footer={
          <div className="flex justify-end w-full">
            <Button
              onClick={onClose}
              className="bg-[#111c44] text-white hover:bg-black dark:hover:bg-[#1b254b] border-none px-8 rounded-xl font-bold text-sm shadow-lg shadow-black/20"
            >
              Close
            </Button>
          </div>
        }
      >
        {plan ? (
          <div className="space-y-6 py-4 px-1">
            {/* Header Banner */}
            <div className="flex items-center justify-between p-6 rounded-2xl bg-slate-900 dark:bg-[#1b254b] text-white shadow-sm overflow-hidden relative group">
              <div className="absolute top-0 right-0 p-8 opacity-5 transform translate-x-4 -translate-y-4 group-hover:scale-110 transition-transform">
                <Activity size={100} />
              </div>
              <div className="relative z-10 flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-white/10 dark:bg-white/5 backdrop-blur-md flex items-center justify-center font-bold text-2xl border border-white/10 dark:border-white/5">
                  {plan.PlanName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-bold tracking-tight mb-1">
                    {plan.PlanName}
                  </h2>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2.5 py-1 rounded-lg font-semibold text-xs uppercase tracking-wider border ${
                        plan.IsActive
                          ? "bg-emerald-500/20 text-emerald-100 border-emerald-500/30"
                          : "bg-rose-500/20 text-rose-100 border-rose-500/30"
                      }`}
                    >
                      {plan.IsActive ? "ACTIVE" : "INACTIVE"}
                    </span>
                    {plan.IsLifetime && (
                      <span className="px-2.5 py-1 rounded-lg font-semibold text-xs uppercase tracking-wider border bg-blue-500/20 text-blue-100 border-blue-500/30">
                        LIFETIME
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="relative z-10 text-right hidden md:block">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Price
                </div>
                <div className="text-3xl font-bold text-white flex items-center gap-1">
                  <span className="text-lg">₹</span>
                  {plan.Price}
                </div>
              </div>
            </div>

            {/* Detail Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderDetailItem(
                "Price",
                `₹ ${plan.Price}`,
                <IndianRupee size={12} />,
              )}
              {renderDetailItem(
                "Duration",
                plan.IsLifetime ? "Lifetime" : `${plan.DurationMonths} Months`,
                plan.IsLifetime ? <Lock size={12} /> : <Calendar size={12} />,
              )}
              {renderDetailItem(
                "Max Users",
                (plan as any).MaxUsers ?? "—",
                <Users size={12} />,
              )}
              {renderDetailItem(
                "Max Rooms",
                (plan as any).MaxRooms ?? "—",
                <Home size={12} />,
              )}
              {renderDetailItem(
                "Created",
                new Date(plan.CreatedAt).toLocaleDateString("en-IN", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }),
                <Clock size={12} />,
              )}
              {renderDetailItem(
                "Plan ID",
                `#${plan.PlanID.toString().padStart(4, "0")}`,
              )}
            </div>

            {/* Description */}
            {plan.Description && (
              <div className="p-5 rounded-2xl bg-white dark:bg-[#111c44] border border-slate-200 dark:border-white/10 shadow-xs">
                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-3">
                  <FileText size={16} />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Description
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400 leading-relaxed">
                  {plan.Description}
                </p>
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    );
  });

SubscriptionDetailsModal.displayName = "SubscriptionDetailsModal";
